import type { LeadDepositRanking } from '@/lib/types';

/**
 * O ranking de clientes em CSV, para fechar a conta do leilão numa planilha.
 * Ponto e vírgula e vírgula decimal: é o que o Excel em português abre em
 * colunas, sem importar. O BOM avisa que é UTF-8 — sem ele, os acentos quebram.
 */
const reais = (cents: number) => (cents / 100).toFixed(2).replace('.', ',');

/**
 * Aspas em volta de tudo que é texto: um nome com ";" não pode virar duas
 * colunas. E o que começa como fórmula (=, +, -, @) ganha um apóstrofo: o nome
 * vem de cadastro e de importação, e o Excel executaria.
 */
const text = (value: string | null) => {
  const safe = /^[=+\-@\t\r]/.test(value ?? '') ? `'${value}` : value ?? '';
  return `"${safe.replace(/"/g, '""')}"`;
};

/** "2026-10-20T15:30:00.000Z" (wall-clock) → "20/10/2026 15:30". */
const dateTime = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}` : '');

const HEADER = [
  'Posição', 'Cliente', 'Contato', 'Responsável', 'Depositado (R$)', 'Depósitos', 'Maior depósito (R$)',
  'Último depósito', 'Participação (%)', 'Saldo informado hoje (R$)',
];

export function leadDepositCsv(ranking: LeadDepositRanking): string {
  const rows = ranking.entries.map((entry) => [
    entry.position,
    text(entry.lead.name),
    text(entry.lead.contact),
    text(entry.owner?.name ?? 'Sem responsável'),
    reais(entry.depositTotal),
    entry.depositCount,
    reais(entry.largestDeposit),
    dateTime(entry.lastDepositAt),
    (entry.share * 100).toFixed(1).replace('.', ','),
    // Como na tela: saldo zero é "não informado", célula vazia.
    entry.balance ? reais(entry.balance) : '',
  ].join(';'));
  return `﻿${[HEADER.join(';'), ...rows].join('\r\n')}\r\n`;
}

const TYPE_SLUGS: Record<string, string> = {
  DEPOSIT: 'deposito-sem-tipo',
  WELCOME: 'boas-vindas',
  EVENT: 'evento',
  UNA_DEPOSIT: 'una-markets',
};

/** O nome do arquivo diz o recorte: "depositos-por-cliente-evento-2026-10-01-a-2026-10-29.csv". */
export function leadDepositFileName(ranking: LeadDepositRanking): string {
  const from = ranking.period.from?.slice(0, 10);
  const to = ranking.period.to?.slice(0, 10);
  const span = from && to ? `${from}-a-${to}` : from ? `desde-${from}` : to ? `ate-${to}` : 'todo-o-periodo';
  const type = ranking.type ? `${TYPE_SLUGS[ranking.type]}-` : '';
  return `depositos-por-cliente-${type}${span}.csv`;
}

export function downloadLeadDepositCsv(ranking: LeadDepositRanking): void {
  const url = URL.createObjectURL(new Blob([leadDepositCsv(ranking)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = leadDepositFileName(ranking);
  link.click();
  URL.revokeObjectURL(url);
}
